import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
    Select,
    FormControl,
    InputLabel,
    MenuItem,
    Card,
    CardHeader,
    CardContent,
    Chip,
    Avatar,
    List,
    ListItem,
    Button,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { MonetizationOn, Clear } from '@material-ui/icons';
import localforage from 'localforage';
import PropTypes from 'prop-types';
import styles from 'styles/pages/comparateur.module.scss';
import getBrandsModelsForComparison from 'lib/getBrandsModelsForComparison';
import { numberFrance } from 'tools/functions';
import { CONVERSION_FUEL } from 'parameters';
import ModelSpecs from 'components/modelSpecs';
import {
    actionGetModel,
    actionSetGetModelToNull,
    actionGetModelTrims,
    actionSetGetModelTrimsToNull,
} from 'store/actions';
import NotifierDialog from 'components/notifierDialog';
import Loading from 'components/loading';
import Breadcrumb from 'components/breadcrumb';
import ModelTrims from 'components/modelTrims';

const WidgetNav = dynamic(() => import('../components/widgetNav'), {
    ssr: false,
});

const WidgetLaunches = dynamic(() => import('../components/widgetLaunches'), {
    ssr: false,
});

const WidgetPromo = dynamic(() => import('../components/widgetPromotion'), {
    ssr: false,
});

const VersionList = ({ model, handleVersionSelect }) => {
    return (
        <div className={styles.rootVL}>
            {model.versions.map((version) => (
                <Card key={version.id}>
                    <CardHeader
                        title={
                            <Button
                                size="small"
                                id={version.id}
                                variant="outlined"
                                onClick={handleVersionSelect}
                            >
                                {version.version}
                            </Button>
                        }
                    />
                    <CardContent>
                        <List className={styles.ul}>
                            <ListItem>{`${version.motor.power} ch`}</ListItem>
                            <ListItem>{`PF ${version.CF.CF}`}</ListItem>
                            <ListItem>
                                {`${CONVERSION_FUEL[version.motor.fuel]}`}
                            </ListItem>
                            <ListItem className={styles.prices}>
                                {`${numberFrance(version.prices.edges[0].node.price)} DH`}
                                {version.prices.edges[0].node.promo && (
                                    <Chip
                                        size="small"
                                        avatar={
                                            <Avatar>
                                                <MonetizationOn />
                                            </Avatar>
                                        }
                                        label="PROMO"
                                        color="secondary"
                                    />
                                )}
                            </ListItem>
                        </List>
                    </CardContent>
                </Card>
            ))}
        </div>
    );
};

VersionList.propTypes = {
    model: PropTypes.object.isRequired,
    handleVersionSelect: PropTypes.func.isRequired,
};

const Comparison = (props) => {
    const {
        brands,
        dataGetModel,
        errorGetModel,
        dataGetModelTrims,
        errorGetModelTrims,
        isLoading,
    } = props;

    const [selectedValues, setSelectedValues] = useState(['all', 'all', 'all']);
    const [selectedModels, setSelectedModels] = useState([null, null, null]);
    const [isVersionList, setIsVersionList] = useState(false);
    const [modelIndex, setModelIndex] = useState(0);
    const [requestedModelId, setRequestedModelId] = useState(null);
    const [modelData, setModelData] = useState([]);
    const [selectedVersions, setSelectedVersions] = useState([]);
    const [selectedVersionIds, setSelectedVersionIds] = useState([]);
    const [localStorage, setLocalStorage] = useState([]);
    const [notification, setNotification] = useState({
        status: '',
        title: '',
        message: '',
        errors: {},
    });

    useEffect(() => {
        if (dataGetModel) {
            const currentModels = [...modelData];
            currentModels[modelIndex] = dataGetModel;
            setModelData([...currentModels]);
            props.actionGetModelTrims(requestedModelId);
            props.actionSetGetModelToNull();
        }
        if (errorGetModel) {
            setNotification({
                status: 'ok_and_dismiss',
                title: 'Il a eu une erreur',
                message: 'Merci de ressayer',
                errors: {},
            });
            props.actionSetGetModelToNull();
        }
    }, [dataGetModel, errorGetModel]);

    useEffect(() => {
        if (dataGetModelTrims) {
            const currentModels = [...modelData];
            currentModels[modelIndex].versions.forEach((version) => {
                const vers = dataGetModelTrims.versions.filter((ver) => {
                    return ver.id === version.id;
                });
                // eslint-disable-next-line no-param-reassign
                version.trims = vers[0].trims;
            });
            setModelData([...currentModels]);
            props.actionSetGetModelTrimsToNull();
        }
        if (errorGetModelTrims) {
            setNotification({
                status: 'ok_and_dismiss',
                title: 'Il a eu une erreur',
                message: 'Merci de ressayer',
                errors: {},
            });
            props.actionSetGetModelTrimsToNull();
        }
    }, [dataGetModelTrims, errorGetModelTrims]);

    useEffect(() => {
        if (modelData[modelIndex] && selectedVersionIds[modelIndex]) {
            const selected = modelData[modelIndex].versions.filter((version) => {
                return version.id === selectedVersionIds[modelIndex];
            });
            if (selected.length > 0) {
                const currentVersions = [...selectedVersions];
                currentVersions[modelIndex] = selected[0];
                setSelectedVersions([...currentVersions]);
            }
        }
    }, [modelData, selectedVersionIds]);

    const handleSelectChangeFromStorage = (index) => {
        localforage.getItem('modelsForComparison').then((value) => {
            const currentSelected = [...selectedValues];
            currentSelected[index] = value[index];
            setSelectedValues([...currentSelected]);
            setModelIndex(index);
            let selectedModel = [];
            brands.forEach((brand) => {
                if (selectedModel.length === 0) {
                    selectedModel = brand.models.filter((mod) => {
                        return mod.id === value[index];
                    });
                    if (selectedModel.length === 1) {
                        selectedModel[0].brand = brand.brand;
                    }
                }
            });
            props.actionGetModel(value[index]);
            setRequestedModelId(value[index]);
            const currentModels = [...selectedModels];
            currentModels[index] = selectedModel[0];
            setSelectedModels([...currentModels]);
            setIsVersionList(true);
        });
    };

    useEffect(() => {
        localforage.getItem('modelsForComparison').then((value) => {
            if (value && value.length > 0) {
                (async function awaitSet() {
                    await setLocalStorage([...value]);
                })();
                handleSelectChangeFromStorage(0);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (
            localStorage.length > 0 &&
            modelData.length > 0 &&
            modelData.length === selectedVersionIds.length &&
            modelData.length < localStorage.length
        ) {
            handleSelectChangeFromStorage(modelData.length);
            if (modelData.length === localStorage.length - 1) {
                setLocalStorage([]);
                localforage.removeItem('modelsForComparison');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modelData, localStorage, selectedVersionIds]);

    const handleSelectChange = (event) => {
        const currentSelected = [...selectedValues];
        const currentSelect = parseInt(event.target.name.replace('select-', ''), 10);
        currentSelected[currentSelect] = event.target.value;
        setSelectedValues([...currentSelected]);
        setModelIndex(currentSelect);
        if (event.target.value !== 'all') {
            let selectedModel = [];
            brands.forEach((brand) => {
                if (selectedModel.length === 0) {
                    selectedModel = brand.models.filter((mod) => {
                        return mod.id === event.target.value;
                    });
                    if (selectedModel.length === 1) {
                        selectedModel[0].brand = brand.brand;
                    }
                }
            });
            props.actionGetModel(event.target.value);
            setRequestedModelId(event.target.value);
            const currentModels = [...selectedModels];
            currentModels[currentSelect] = selectedModel[0];
            setSelectedModels([...currentModels]);
            setIsVersionList(true);
        } else {
            const currentModels = [...selectedModels];
            currentModels[currentSelect] = null;
            setSelectedModels([...currentModels]);
            const currentVersions = [...selectedVersions];
            currentVersions.pop();
            setSelectedVersions([...currentVersions]);
            const currentModelData = [...modelData];
            currentModelData.pop();
            setModelData([...currentModelData]);
        }
    };
    const handleSetSelect = () => {
        const options = [
            <MenuItem key={0} aria-label="Choisir" value="all">
                Choisir
            </MenuItem>,
        ];
        brands.forEach((brand) => {
            brand.models.forEach((mod) => {
                options.push(
                    <MenuItem value={mod.id} key={mod.id}>
                        {`${brand.brand} ${mod.model}`}
                    </MenuItem>,
                );
            });
        });

        return options;
    };
    const handleVersionSelect = (event) => {
        setIsVersionList(false);
        const currentVersionIds = [...selectedVersionIds];
        currentVersionIds[modelIndex] = event.target.id;
        setSelectedVersionIds([...currentVersionIds]);
    };
    const handleSetSelectedDisabled = (ind) => {
        const mods = selectedVersions.length;
        if (ind === 0 && [2, 3].includes(mods)) {
            return true;
        }
        if (ind === 1 && [0, 3].includes(mods)) {
            return true;
        }
        if (ind === 2 && [0, 1].includes(mods)) {
            return true;
        }
        return false;
    };
    const handleClearAll = () => {
        setSelectedValues(['all', 'all', 'all']);
        setSelectedModels([null, null, null]);
        setIsVersionList(false);
        setModelIndex(0);
        setModelData([]);
        setSelectedVersions([]);
        setSelectedVersionIds([]);
    };
    const handleNotificationDismiss = () => {
        setNotification({
            status: '',
            title: '',
            message: '',
            errors: {},
        });
    };
    return (
        <div>
            <Head>
                <title>
                    Comparateur voiture neuve au Maroc | prix, caractéristiques
                    techniques, équipements
                </title>
                <meta
                    name="description"
                    content="Comparateur toutes les voitures neuves, tous segments et types au Maroc.  "
                />
                <meta property="og:type" content="siteweb" />
                <meta
                    property="og:title"
                    content="Comparateur voiture neuve au Maroc, prix, caractéristiques techniques, équipements"
                />
                <meta
                    property="og:image"
                    content={`${process.env.NEXT_PUBLIC_API_HOST}/images/og/comparateur.png`}
                />
                <meta
                    property="og:url"
                    content={`${process.env.NEXT_PUBLIC_CLIENT_HOST}/comparateur-voiture`}
                />
                <link
                    rel="canonical"
                    href={`${process.env.NEXT_PUBLIC_CLIENT_HOST}/comparateur-voiture`}
                />
            </Head>
            <main className={styles.root}>
                <Breadcrumb
                    links={[
                        {
                            href: null,
                            text: 'Comparateur voitures',
                        },
                    ]}
                />
                {selectedVersionIds.length > modelData.length && isLoading && <Loading />}
                <div className="main-title">
                    <h1>Comparateur voiture neuve au Maroc</h1>
                </div>
                {localStorage.length > 0 && (
                    <Alert
                        style={{ maxWidth: 500, margin: '0 auto', textAlign: 'left' }}
                        severity="error"
                        variant="filled"
                    >
                        Pour chaque modèle, veuillez choisir la version souhaitée
                    </Alert>
                )}
                <NotifierDialog
                    notification={notification}
                    handleNotificationDismiss={handleNotificationDismiss}
                />
                <div className={styles.mainContainer}>
                    <Card className={styles.cardRoot}>
                        <CardHeader title={<h2>Choisir modèles & versions</h2>} />
                        <CardContent className={styles.cardContent}>
                            <div>
                                <Clear
                                    size="small"
                                    variant="outlined"
                                    onClick={handleClearAll}
                                    disabled={selectedVersions.length === 0}
                                />
                            </div>
                            <div className={styles.selectForms}>
                                {[...Array(3).keys()].map((ind) => (
                                    <form key={ind}>
                                        <FormControl variant="outlined">
                                            <InputLabel id={`select-${ind}-label`}>
                                                Choisir modèle
                                            </InputLabel>
                                            <Select
                                                labelId={`select-${ind}-label`}
                                                name={`select-${ind}`}
                                                label="Choisir modèle"
                                                value={selectedValues[ind]}
                                                onChange={handleSelectChange}
                                                variant="outlined"
                                                disabled={handleSetSelectedDisabled(ind)}
                                            >
                                                {handleSetSelect()}
                                            </Select>
                                            <span
                                                id="no_cat_search"
                                                className="form_error"
                                            />
                                        </FormControl>
                                    </form>
                                ))}
                            </div>
                            {isVersionList && (
                                <VersionList
                                    model={selectedModels[modelIndex]}
                                    handleVersionSelect={handleVersionSelect}
                                />
                            )}
                            <div className={styles.selectForms}>
                                <div>
                                    {modelData[0] && (
                                        <Image
                                            src={`${process.env.NEXT_PUBLIC_API_HOST}/images/models/${modelData[0].images[0].filename}`}
                                            alt={`${modelData[0].brand.brand}-${modelData[0].model}`}
                                            width={150}
                                            height={100}
                                        />
                                    )}
                                </div>
                                <div>
                                    {modelData[1] && (
                                        <Image
                                            src={`${process.env.NEXT_PUBLIC_API_HOST}/images/models/${modelData[1].images[0].filename}`}
                                            alt={`${modelData[1].brand.brand}-${modelData[1].model}`}
                                            width={150}
                                            height={100}
                                        />
                                    )}
                                </div>
                                <div>
                                    {modelData[2] && (
                                        <Image
                                            src={`${process.env.NEXT_PUBLIC_API_HOST}/images/models/${modelData[2].images[0].filename}`}
                                            alt={`${modelData[2].brand.brand}-${modelData[2].model}`}
                                            width={150}
                                            height={100}
                                        />
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className={styles.cardRoot}>
                        <CardHeader title={<h2>Caractéristiques techniques</h2>} />
                        <CardContent className={styles.cardContent}>
                            {selectedVersions.length > 0 ? (
                                <ModelSpecs versions={selectedVersions} />
                            ) : (
                                <div className={styles.chipContainer}>
                                    <Chip size="small" label="Aucune version" />
                                </div>
                            )}
                        </CardContent>
                    </Card>
                    <Card className={styles.cardRoot}>
                        <CardHeader title={<h2>Équipements</h2>} />
                        <CardContent className={styles.cardContent}>
                            {selectedVersions.length > 0 &&
                            selectedVersions[modelIndex]?.trims ? (
                                <ModelTrims versions={selectedVersions} />
                            ) : (
                                <div className={styles.chipContainer}>
                                    <Chip size="small" label="Aucune version" />
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
                <WidgetPromo data={brands} />
                <WidgetNav brands={brands} />
                <WidgetLaunches data={brands} />
            </main>
        </div>
    );
};

Comparison.propTypes = {
    brands: PropTypes.array.isRequired,
    actionGetModel: PropTypes.func.isRequired,
    actionSetGetModelToNull: PropTypes.func.isRequired,
    actionGetModelTrims: PropTypes.func.isRequired,
    actionSetGetModelTrimsToNull: PropTypes.func.isRequired,
    dataGetModel: PropTypes.any,
    errorGetModel: PropTypes.any,
    dataGetModelTrims: PropTypes.any,
    errorGetModelTrims: PropTypes.any,
    isLoading: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => {
    return {
        dataGetModel: state.model.dataGetModel,
        errorGetModel: state.model.errorGetModel,
        dataGetModelTrims: state.model.dataGetModelTrims,
        errorGetModelTrims: state.model.errorGetModelTrims,
        isLoading: state.model.isLoading,
    };
};

function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            actionGetModel,
            actionSetGetModelToNull,
            actionGetModelTrims,
            actionSetGetModelTrimsToNull,
        },
        dispatch,
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(Comparison);

export async function getStaticProps() {
    let brands = await getBrandsModelsForComparison();
    brands = brands.data.brands;
    return {
        props: {
            brands,
        },
    };
}
